import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { AuthContext } from '../authContext';
import { emailDomain, orgName } from '../brand';
import './AdminDashboard.css';

const STATUS_STYLE = {
  approved: { bg: 'rgba(16,185,129,0.15)', fg: '#059669', label: 'Approved' },
  pending: { bg: 'rgba(234,179,8,0.18)', fg: '#b45309', label: 'Pending' },
  unverified: { bg: 'rgba(148,163,184,0.20)', fg: '#475569', label: 'Awaiting email confirmation' },
  whitelisted: { bg: 'rgba(129,140,248,0.18)', fg: '#4f46e5', label: 'Invited' },
  rejected: { bg: 'rgba(239,68,68,0.15)', fg: '#dc2626', label: 'Rejected' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg: '#e5e7eb', fg: '#374151', label: status || '—' };
  return <span className="admin-badge" style={{ background: s.bg, color: s.fg }}>{s.label}</span>;
}

const ACTION_STYLE = {
  create: { background: 'rgba(16,185,129,0.15)', color: '#059669' },
  edit: { background: 'rgba(59,130,246,0.15)', color: '#2563eb' },
  delete: { background: 'rgba(239,68,68,0.15)', color: '#dc2626' },
};
const actionStyle = (a) => ACTION_STYLE[a] || { background: '#e5e7eb', color: '#374151' };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [activity, setActivity] = useState([]);
  const [recordHistory, setRecordHistory] = useState([]);

  const [inviteInput, setInviteInput] = useState('');
  const [domainInput, setDomainInput] = useState('');

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(null), 3500); };

  const load = useCallback(async () => {
    setError(null);
    try {
      const [dash, us, doms] = await Promise.all([
        apiService.adminDashboard(),
        apiService.adminListUsers(),
        apiService.adminListDomains(),
      ]);
      setDashboard(dash);
      setUsers(us);
      setDomains(doms);
      try {
        const act = await apiService.adminActivity(30);
        setActivity(act.entries || []);
      } catch (_) { /* activity is best-effort */ }
      try {
        const rh = await apiService.adminRecordHistory(50);
        setRecordHistory(rh.entries || []);
      } catch (_) { /* best-effort */ }
    } catch (e) {
      if (e.status === 403 || /admin access/i.test(e.message || '')) setDenied(true);
      else setError(e.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { try { await apiService.adminApproveUser(id); flash('User approved — they were emailed.'); load(); } catch (e) { setError(e.message); } };
  const reject = async (id) => { try { await apiService.adminRejectUser(id); flash('User rejected.'); load(); } catch (e) { setError(e.message); } };
  const del = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"? This removes their account from all of ${orgName()}'s apps and cannot be undone.`)) return;
    try { await apiService.adminDeleteUser(id); flash('User deleted.'); load(); } catch (e) { setError(e.message); }
  };
  const toggleCanDelete = async (u) => {
    const grant = !u.can_delete_records;
    const name = u.email || u.username;
    if (grant && !window.confirm(`Allow ${name} to permanently delete individuals' records?`)) return;
    try {
      await apiService.adminSetCanDelete(u.id, grant);
      flash(grant ? `${name} can now delete records.` : `${name} can no longer delete records.`);
      load();
    } catch (e) { setError(e.message); }
  };
  const invite = async () => {
    const email = inviteInput.trim().toLowerCase();
    if (!email) return;
    try {
      const result = await apiService.adminInviteEmail(email);
      setInviteInput('');
      if (result.invited) flash(`Invited ${email}. They've been emailed a link to choose a password.`);
      else if (result.status === 'approved') flash(`${email} already has an account; no invitation sent.`);
      else flash(`${email} was sent several links in the last hour; try again later.`);
      load();
    }
    catch (e) { setError(e.message); }
  };
  const addDomain = async () => {
    const d = domainInput.trim().toLowerCase().replace(/^@/, '');
    if (!d) return;
    try { await apiService.adminAddDomain(d); setDomainInput(''); flash(`Added domain ${d}.`); load(); }
    catch (e) { setError(e.message); }
  };
  const removeDomain = async (id) => { try { await apiService.adminRemoveDomain(id); load(); } catch (e) { setError(e.message); } };

  if (denied) {
    return (
      <div className="admin-page">
        <div className="admin-denied">
          <h2>Admin access required</h2>
          <p>Your account doesn't have administrator privileges.</p>
          <button className="admin-btn" onClick={() => navigate('/')}>Back to dashboard</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="admin-page"><div className="admin-loading">Loading admin dashboard…</div></div>;
  }

  const pending = users.filter((u) => u.status === 'pending');
  const counts = dashboard?.counts || {};
  const llm = dashboard?.llm || {};

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <h1>Admin</h1>
          <span className="admin-sub">Support Planner · signed in as {auth?.username || 'admin'}</span>
        </div>
        <div className="admin-header-actions">
          <button className="admin-btn ghost" onClick={load}>Refresh</button>
          <button className="admin-btn ghost" onClick={() => navigate('/')}>← Back to app</button>
        </div>
      </header>

      {notice && <div className="admin-notice">{notice}</div>}
      {error && <div className="admin-error">{error}</div>}

      {/* Stat cards */}
      <div className="admin-stats">
        <div className="admin-stat"><div className="admin-stat-num">{counts.users ?? '—'}</div><div className="admin-stat-label">Users</div></div>
        <div className="admin-stat"><div className="admin-stat-num">{counts.individuals ?? '—'}</div><div className="admin-stat-label">Individuals</div></div>
        <div className="admin-stat"><div className="admin-stat-num">{counts.care_sections ?? '—'}</div><div className="admin-stat-label">Care sections</div></div>
        <div className="admin-stat"><div className="admin-stat-num">${(llm.total_estimated_cost ?? 0).toFixed(2)}</div><div className="admin-stat-label">LLM spend</div></div>
      </div>

      {/* Pending approvals */}
      <section className="admin-card">
        <h2>Pending approvals {pending.length > 0 && <span className="admin-count">{pending.length}</span>}</h2>
        {pending.length === 0 ? (
          <p className="admin-empty">No one is waiting for approval.</p>
        ) : (
          <div className="admin-list">
            {pending.map((u) => (
              <div key={u.id} className="admin-list-row">
                <div className="admin-list-main">
                  <span className="admin-list-name">{u.email || u.username}</span>
                  <span className="admin-list-meta">requested {u.created_at ? new Date(u.created_at).toLocaleString() : '—'}</span>
                </div>
                <div className="admin-list-actions">
                  <button className="admin-btn approve" onClick={() => approve(u.id)}>Approve</button>
                  <button className="admin-btn danger" onClick={() => reject(u.id)}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Access management: invitations + domains */}
      <section className="admin-card">
        <h2>Access management</h2>

        <div className="admin-field">
          <label>Invite someone by email</label>
          <div className="admin-inline">
            <input
              type="email"
              placeholder={`email@${emailDomain()}`}
              value={inviteInput}
              onChange={(e) => setInviteInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') invite(); }}
            />
            <button className="admin-btn" onClick={invite}>Invite</button>
          </div>
          <p className="admin-hint">They're emailed a link to choose a password and are in as soon as they do (the link lasts 7 days). Works for any address, even if its domain isn't on the list below.</p>
        </div>

        <div className="admin-field">
          <label>Allowed registration domains</label>
          <div className="admin-inline">
            <input
              type="text"
              placeholder={emailDomain()}
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addDomain(); }}
            />
            <button className="admin-btn" onClick={addDomain}>Add domain</button>
          </div>
          <div className="admin-chips">
            {domains.length === 0 && <span className="admin-empty">No domains — only invited people can join.</span>}
            {domains.map((d) => (
              <span key={d.id} className="admin-chip">
                {d.domain}
                <button className="admin-chip-x" title="Remove" onClick={() => removeDomain(d.id)}>×</button>
              </span>
            ))}
          </div>
          <p className="admin-hint">Anyone with an email at these domains can sign up: they confirm the address through an emailed link, choose a password, and are in — no approval needed.</p>
        </div>
      </section>

      {/* All users */}
      <section className="admin-card">
        <h2>All users <span className="admin-count">{users.length}</span></h2>
        <p className="admin-hint" style={{ marginTop: '-8px', marginBottom: '6px' }}>
          Accounts are shared by all of {orgName()}'s apps: approving, removing or inviting someone here applies everywhere.
        </p>
        <p className="admin-hint" style={{ marginTop: 0, marginBottom: '14px' }}>
          "Can delete records" lets a user permanently delete an individual and their support plan (they turn on Delete mode on the Support Plans page). Admins always can. Every deletion is recorded in the change history below.
        </p>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Email / username</th><th>Status</th><th>Role</th><th>Can delete records</th><th>Last login</th><th></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email || u.username}</td>
                  <td><StatusBadge status={u.status} /></td>
                  <td>{u.is_admin ? <span className="admin-badge" style={{ background: 'rgba(134,54,152,0.15)', color: 'var(--cor-purple, #863698)' }}>Admin</span> : 'User'}</td>
                  <td>
                    {u.is_admin ? (
                      <span className="admin-muted">Always</span>
                    ) : (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!u.can_delete_records} onChange={() => toggleCanDelete(u)} />
                        {u.can_delete_records ? 'Yes' : 'No'}
                      </label>
                    )}
                  </td>
                  <td className="admin-muted">{u.last_login ? new Date(u.last_login).toLocaleString() : 'never'}</td>
                  <td>{!u.is_admin && <button className="admin-btn danger small" onClick={() => del(u.id, u.email || u.username)}>Delete</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Record change history — separate per-record audit trail */}
      {recordHistory.length > 0 && (
        <section className="admin-card">
          <h2>Record change history <span className="admin-count">{recordHistory.length}</span></h2>
          <p className="admin-hint" style={{ marginTop: '-8px', marginBottom: '14px' }}>
            Attributable log of who edited or deleted individual records + care-plan content — kept separate from the activity feed below.
          </p>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>When</th><th>Who</th><th>Action</th><th>Record</th><th>Details</th></tr>
              </thead>
              <tbody>
                {recordHistory.map((r) => (
                  <tr key={r.id}>
                    <td className="admin-muted">{r.timestamp ? new Date(r.timestamp).toLocaleString() : ''}</td>
                    <td>{r.username || 'system'}</td>
                    <td><span className="admin-badge" style={actionStyle(r.action)}>{r.action}</span></td>
                    <td>{(r.individual_name || r.entity_type)}{r.section ? ` › ${r.section}` : ''}</td>
                    <td className="admin-muted">{r.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Recent activity */}
      {activity.length > 0 && (
        <section className="admin-card">
          <h2>Recent activity</h2>
          <div className="admin-activity">
            {activity.map((a) => (
              <div key={a.id} className="admin-activity-row">
                <span className="admin-activity-action">{a.action}</span>
                <span className="admin-activity-meta">
                  {a.username || 'system'}{a.details ? ` · ${a.details}` : ''}
                </span>
                <span className="admin-activity-time">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
